import React, { useState, useEffect } from "react";
import { useApi } from "../utils/Api";
import { useCart } from "../context/CartContext";
import { useCheckout } from "../context/CheckoutContext";
import ErrorModal from "./ErrorModal";
import ConfirmModal from "./ConfirmModal";
import SuccessModal from "./SuccessModal";

// Spinner overlay component
const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

const OrderHistory = ({ user, closeProfile }) => {
  const { populateCartFromReorder } = useCart();
  const { openCheckout } = useCheckout();
  const { apiFetch } = useApi();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Archive state
  const [archiveOrderId, setArchiveOrderId] = useState(null);
  const [archiving, setArchiving] = useState(false);
  // Receipt actions spinner
  const [receiptLoading, setReceiptLoading] = useState(false);

  // --- Fetch customer orders ---
  useEffect(() => {
    if (!user?.id) return;

    let isMounted = true;

    const fetchOrders = async () => {
      setLoading(true);
      try {
        const data = await apiFetch("get-customer-orders", {
          customer_id: user.id,
        });

        if (isMounted) {
          if (data.status === "success" && Array.isArray(data.orders)) {
            setOrders(data.orders);
          } else {
            setOrders([]);
          }
        }
      } catch (err) {
        console.error("Failed to load orders:", err);
        if (isMounted) setOrders([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchOrders();

    return () => {
      isMounted = false;
    };
  }, [user?.id, apiFetch]);

  const toggleOrder = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  // --- STOCK CHECK ---
  const checkStockBeforeReorder = async (orderItems) => {
    try {
      const data = await apiFetch("check-stock", { cartItems: orderItems });

      if (!data || data.success !== true) return true;

      if (data.soldOutItems?.length > 0) {
        const names = data.soldOutItems
          .map((item) => item.item_name)
          .join(", ");

        setErrorMessage(
          <>
            The following items are sold out: <b>{names}</b>.
            <br />
            Please remove them from your basket to proceed.
          </>,
        );
        return false;
      }

      return true;
    } catch (err) {
      console.warn("Stock check error:", err);
      return true;
    }
  };

  // --- Reorder ---
  const reorder = async (order) => {
    setReorderingId(order.id);

    try {
      const newCartItems = await populateCartFromReorder(order);
      const canReorder = await checkStockBeforeReorder(newCartItems);

      if (!canReorder) {
        setReorderingId(null);
        return;
      }

      closeProfile();
      openCheckout(null);
    } catch (err) {
      console.error("Reorder failed:", err);
    } finally {
      setReorderingId(null);
    }
  };

  const archiveOrder = async (orderId) => {
    try {
      const data = await apiFetch("archive-order", {
        order_id: orderId,
      });

      if (data.success) {
        setOrders((prevOrders) => prevOrders.filter((o) => o.id !== orderId));
      } else {
        console.warn("Failed to archive order:", data.message);
      }
    } catch (err) {
      console.error("API error:", err);
    }
  };

  const emailReceipt = async (orderId) => {
    setReceiptLoading(true);
    try {
      const res = await apiFetch("email-receipt", { order_id: orderId });

      if (res?.success) {
        setSuccessMessage(
          <>
            Receipt sent successfully 🎉 <br />
            <small>Please check your email inbox.</small>
          </>,
        );
      } else {
        setErrorMessage(res?.message || "Failed to send receipt.");
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to send receipt.");
    } finally {
      setReceiptLoading(false);
    }
  };

  const printReceipt = async (orderId, orderNumber) => {
    setReceiptLoading(true);
    const oldTitle = document.title;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    try {
      const res = await apiFetch("get-receipt-html", { order_id: orderId });
      if (!res?.success || !res?.html) {
        setErrorMessage(res?.message || "Failed to load receipt.");
        return;
      }

      document.title = `Lasgidi Receipt #${orderNumber}`;

      // Make it a real HTML document URL
      const blob = new Blob([res.html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      // ✅ Mobile/iOS: open in a new tab then print from there (much more reliable)
      if (isMobile || isIOS) {
        const w = window.open(url, "_blank");
        if (!w) {
          setErrorMessage(
            "Popup blocked. Please allow popups to print receipts.",
          );
          URL.revokeObjectURL(url);
          return;
        }

        // Wait for the page to load then print
        w.onload = () => {
          try {
            w.focus();
            w.print();
          } catch (e) {
            // If print is blocked, user can use Share/Print manually
          }
        };

        // Cleanup later
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }

      // ✅ Desktop: your iframe approach is fine
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
        }, 500);
      };

      iframe.src = url;
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to load receipt.");
    } finally {
      setTimeout(() => {
        document.title = oldTitle;
        setReceiptLoading(false);
      }, 800);
    }
  };

  if (loading) return <p>Loading orders...</p>;
  if (!orders.length)
    return <p className="no-orders">You haven’t placed any orders yet.</p>;

  return (
    <>
      <ul className="orders-list">
        {orders.map((order) => (
          <li
            key={order.id}
            className="order-card"
            onClick={() => toggleOrder(order.id)}
          >
            <div className="order-date">
              {new Date(order.created_at).toLocaleDateString("en-GB")}
            </div>

            <div className="order-header">
              <strong>Order #{order.order_number}</strong>{" "}
              <span>{order.order_type}</span>
              {/* ARCHIVE ICON BUTTON (CARD LEVEL) */}
              <button
                className="archive-order-icon delete-btn"
                title="Archive order"
                onClick={(e) => {
                  e.stopPropagation();
                  setArchiveOrderId(order.id);
                }}
              >
                <i className="fas fa-archive"></i>
              </button>
            </div>

            <p className="order-total">
              <strong>£{parseFloat(order.total).toFixed(2)}</strong>
            </p>

            {expandedOrderId === order.id && (
              <div className="profile-order-items">
                {order.items.map((item) => {
                  const optionTotal = [
                    ...item.selectedOptionalOptions,
                    ...item.swallowOptions,
                    ...item.proteins,
                  ].reduce((sum, opt) => sum + opt.price * opt.quantity, 0);

                  const itemTotal =
                    (parseFloat(item.base_price || 0) +
                      parseFloat(item.required_option_price || 0) +
                      optionTotal) *
                    (item.quantity || 1);

                  const mainProteinIds = [103, 104, 105, 106, 107, 108];
                  const mainProteins = item.proteins.filter((p) =>
                    mainProteinIds.includes(Number(p.id)),
                  );
                  const extraProteins = item.proteins.filter(
                    (p) => !mainProteinIds.includes(Number(p.id)),
                  );

                  return (
                    <div key={item.id} className="profile-order-item">
                      <div className="profile-order-item-header">
                        <span className="profile-order-item-title">
                          {item.name} (£
                          {parseFloat(item.base_price || 0).toFixed(2)})
                        </span>
                      </div>

                      {item.required_option_id && (
                        <div className="profile-order-item-option">
                          <ul>
                            <li>
                              {item.required_option_name} x {item.quantity} (£
                              {(
                                parseFloat(item.required_option_price || 0) *
                                item.quantity
                              ).toFixed(2)}
                              )
                            </li>
                          </ul>
                        </div>
                      )}

                      {item.selectedOptionalOptions.length > 0 && (
                        <div className="profile-order-item-option">
                          <strong>Optional:</strong>
                          <ul>
                            {item.selectedOptionalOptions.map((opt) => (
                              <li key={opt.id}>
                                {opt.name} x {opt.quantity} (£
                                {(opt.price * opt.quantity).toFixed(2)})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {item.swallowOptions.length > 0 && (
                        <div className="profile-order-item-option">
                          <strong>Swallow:</strong>
                          <ul>
                            {item.swallowOptions.map((sw) => (
                              <li key={sw.id}>
                                {sw.name} x {sw.quantity} (£
                                {(sw.price * sw.quantity).toFixed(2)})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {mainProteins.length > 0 && (
                        <div className="profile-order-item-option">
                          <strong>Proteins:</strong>
                          <ul>
                            {mainProteins.map((p) => (
                              <li key={p.id}>
                                {p.name} x {p.quantity} (£
                                {(p.price * p.quantity).toFixed(2)})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {extraProteins.length > 0 && (
                        <div className="profile-order-item-option">
                          <strong>Extra:</strong>
                          <ul>
                            {extraProteins.map((p) => (
                              <li key={p.id}>
                                {p.name} x {p.quantity} (£
                                {(p.price * p.quantity).toFixed(2)})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="profile-order-item-total-wrapper">
                        <span className="profile-order-item-total-text">
                          x {item.quantity} = £{itemTotal.toFixed(2)}
                        </span>
                      </div>

                      <hr className="profile-order-item-divider" />
                    </div>
                  );
                })}

                <div className="profile-order-actions">
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="track-order-button"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="fas fa-map-marker-alt"></i> Track Order
                  </a>

                  <button
                    className="add-to-cart-button reorder-btn"
                    disabled={reorderingId === order.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      reorder(order);
                    }}
                  >
                    {reorderingId === order.id ? "Adding..." : "Re-order"}
                  </button>
                </div>

                <div className="receipt-buttons">
                  <button
                    className="receipt-btn"
                    disabled={receiptLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      printReceipt(order.id, order.order_number);
                    }}
                  >
                    <i className="fas fa-print"></i> Print Receipt
                  </button>

                  <button
                    className="receipt-btn"
                    disabled={receiptLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      emailReceipt(order.id);
                    }}
                  >
                    <i className="fas fa-envelope"></i> Email Receipt
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {errorMessage && (
        <ErrorModal
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}

      {successMessage && (
        <SuccessModal
          message={successMessage}
          onClose={() => setSuccessMessage(null)}
        />
      )}

      {archiveOrderId && (
        <ConfirmModal
          title="Archive order"
          message={
            <>
              Are you sure you want to archive this order?
              <br />
              <small>This will hide it from your order history.</small>
            </>
          }
          confirmText="Archive"
          cancelText="Cancel"
          loading={archiving}
          onCancel={() => {
            setArchiveOrderId(null);
            setArchiving(false);
          }}
          onConfirm={async () => {
            setArchiving(true);
            await archiveOrder(archiveOrderId);
            setArchiving(false);
            setArchiveOrderId(null);
          }}
        />
      )}

      {/* Show full-page spinner overlay during reorder or archive */}
      {(archiving || reorderingId || receiptLoading) && <SpinnerOverlay />}
    </>
  );
};

export default OrderHistory;
